import React, { useContext, useEffect, useState } from 'react';
import { isEmpty, isEqual, isNil } from 'lodash';
import { connect } from 'react-redux';
import axios from 'axios';
import log from 'chameleon/ui-stack/utilities/log';
import { ResultsContext } from './ResultsDataProvider';
import { getPerformanceStacks } from 'legacy-src/components/assessment/Results/utility';
import { isImpersonating } from 'legacy-src/components/impersonating/utils';
import { isFRQ } from 'legacy-src/utility/progressHelpers';
import HorizontalTabs from 'chameleon/ui-stack/ui/base/tabs/horizontal-tabs';
import { BreadcrumbContext } from 'chameleon/ui-stack/ui/basic/breadcrumbs';
import StudentResultsHeader from './StudentResultsHeader';
import { getPriorStudentsSubdir } from 'legacy-src/utility/utils';
import { Feedback } from 'legacy-src/components/assessment/Results/AggregateResults/Student/Feedback';
import useBreadcrumbNavigation from './hooks/useBreadcrumbNavigation';

const StudentResultsContainer = (props) => {
  const resultsData = useContext(ResultsContext);
  const {
    assignment,
    params,
    routes,
    router,
    courseOutlineConfig,
    isPPC,
    isQB,
    learnosityReportsData,
    hasFrqQuestions,
    location: { pathname },
    meQuery: {
      me: { initId, isStudent, firstName, lastName },
    },
    students = [{ id: initId, firstName, lastName }],
    showFrqResponsesStudent,
    masteryReportFlag,
    hasScramblePerStudent,
    questions,
    tagPairsData: {
      assignmentProgressWithTagPairs: { otherParts },
    },
    studentsBySection,
    reviewModeFlag,
    immediateFeedbackFlag,
  } = props;

  const [customOrder, setCustomOrder] = useState(null);
  const [orderedQuestions, setOrderedQuestions] = useState([]);
  const [orderedAssignment, setOrderedAssignment] = useState(assignment);
  const { dispatch, breadCrumbs, ACTIONS } = useContext(BreadcrumbContext);

  useBreadcrumbNavigation(router, dispatch, breadCrumbs, ACTIONS);

  useEffect(() => {
    if (customOrder) {
      const newOrderedQuestions = [];
      questions.forEach((question) => {
        newOrderedQuestions[customOrder.indexOf(question.id)] = question;
      });
      if (!isEqual(newOrderedQuestions, orderedQuestions)) {
        setOrderedAssignment({
          ...assignment,
          questions: newOrderedQuestions,
        });
        setOrderedQuestions(newOrderedQuestions);
      }
    } else if (!isEqual(assignment, orderedAssignment)) {
      setOrderedAssignment(assignment);
      setOrderedQuestions(questions);
    }
  }, [customOrder, assignment]);

  const toggleQuestionsOrder = (newOrder) => {
    setCustomOrder(newOrder);
  };

  const { subject, questionId } = params;

  const { id: assignmentId } = orderedAssignment;
  const baseUrl = `/${subject}/assessments/results/${assignmentId}${getPriorStudentsSubdir(
    routes,
  )}/`;

  useEffect(() => {
    // update student review status
    if (!isImpersonating() && isStudent) {
      const studentId = parseInt(initId, 10);
      const score_review_status_url = `${FYM_BACKEND_URL}/assessments/api/assignments/${assignmentId}/score_review_status/${studentId}/`;
      axios
        .put(score_review_status_url, {
          has_student_reviewed_current_score: true,
        })
        .catch((error) => log('error', error));
    }
  }, []);

  useEffect(() => {
    if (pathname.endsWith('performance')) {
      dispatch({ type: ACTIONS.removeAllBreadCrumbs });
    }
  }, [pathname]);

  let tabs = [];

  if (!isQB && isPPC) {
    const resourcesTabLabel =
      (courseOutlineConfig.data || {}).curriculumCheckTabLabel || null;
    tabs.push({
      key: 'topics-and-skills',
      name: resourcesTabLabel,
      isActive: pathname.includes('resources'),
      onClick: () => router.push(`${baseUrl}resources`),
    });
  }

  if (masteryReportFlag) {
    tabs.push({
      key: 'csperformance',
      name: 'Content & Skills Performance',
      isActive: pathname.includes('csperformance'),
      onClick: () => router.push(`${baseUrl}csperformance`),
    });
  }

  const QUESTIONS_TAB_KEY = 'questions';
  tabs.push({
    key: QUESTIONS_TAB_KEY,
    name: 'Questions',
    isActive:
      pathname.includes('performance') && !pathname.includes('csperformance'),
    onClick: () => router.push(`${baseUrl}performance`),
  });

  const studentScore = getPerformanceStacks(learnosityReportsData, initId);

  let isStudentResponseAvailable;
  const { questions: assignmentQuestions } = orderedAssignment;
  if (showFrqResponsesStudent && questionId) {
    const currentQuestion = assignmentQuestions.find(
      ({ id }) => id === questionId,
    );
    isStudentResponseAvailable = isStudent && isFRQ(currentQuestion);
  } else if (showFrqResponsesStudent) {
    isStudentResponseAvailable = isStudent && hasFrqQuestions;
  }

  /**
   * If we only have one tab, it's probably the questions tab. In that
   * case, we should not try to load the Topic and Skills, or Content and
   * Skills reports if that's where the URL is pointing to.
   * Ensure we always land on questions tab in these cases.
   *
   * Typically the student results link to /resources which implies the T&S Report.
   * The second condition ensures we have a default tab when we land on /resources for a non PPC
   */
  if (
    (tabs.length === 1 && tabs[0].key !== QUESTIONS_TAB_KEY) ||
    (pathname.endsWith('resources') && !isPPC)
  ) {
    const routePath = `${baseUrl}performance`;
    dispatch({
      type: ACTIONS.updateResultsOverviewRoute,
      payload: routePath,
    });
    router.replace(routePath);
  }
  const onButtonClick = () => {
    !isEmpty(questions) &&
      router.push(
        `${baseUrl}performance/responses/questions/${questions[0].id}`,
      );
  };

  const componentProps = {
    ...resultsData,
    ...{
      ...props,
      items: customOrder ? orderedQuestions : props.questions,
      questions: customOrder ? orderedQuestions : props.questions,
    },
    assignment: orderedAssignment,
    studentId: initId,
    teacherOtherParts: otherParts,
    isYourResult: true,
    isStudent,
    students,
  };

  const question = React.useRef();
  if (!isNil(params.questionId)) {
    question.current = params.questionId;
  }
  const pushPathname = () =>
    router.push(`${baseUrl}performance/${question.current}`);

  return (
    <React.Fragment>
      <StudentResultsHeader
        {...props}
        hasScramblePerStudent={hasScramblePerStudent}
        toggleQuestionsOrder={toggleQuestionsOrder}
        isStudentResponseAvailable={isStudentResponseAvailable}
        assignment={orderedAssignment}
        studentScore={studentScore}
        onButtonClick={onButtonClick}
        showPerformanceAndTime={!isEmpty(studentScore)}
        isStudent
        studentsBySection={studentsBySection}
        reviewModeFlag={reviewModeFlag}
        immediateFeedbackFlag={immediateFeedbackFlag}
      />
      <Feedback assignment={orderedAssignment} studentId={initId} />
      <HorizontalTabs
        tabs={tabs}
        direction="horizontal"
        accessibility={{
          ariaLabel: 'Student Results Tabs',
        }}
        classNames={{
          tab: 'flex flex-1 justify-center px-5 py-8 tab',
        }}
        tabsClassName="justify-around my-12"
      />
      {React.cloneElement(props.children, { ...componentProps, pushPathname })}
    </React.Fragment>
  );
};

const mapStateToProps = ({ featureFlag, studentsBySection }) => ({
  showFrqResponsesStudent: featureFlag.show_frq_responses_student,
  showFrqResponses: featureFlag.show_frq_responses,
  studentsBySection: studentsBySection || {},
  hasScramblePerStudent: featureFlag.scramble_questions_per_student,
  masteryReportFlag: featureFlag.mastery_report,
  reviewModeFlag: featureFlag.review_mode,
  immediateFeedbackFlag: featureFlag.immediate_feedback,
});

export default connect(mapStateToProps)(StudentResultsContainer);
